import { Injectable } from '@angular/core';
import { EMPTY, Observable, of } from 'rxjs';
import { map, catchError, expand, reduce, shareReplay, tap } from 'rxjs/operators';

import { ApiService } from './api.service';
import { readPage, toQueryParams } from '../alarms-api';
import { BandHistoryQuery, ImeiChangesQuery, WorkHistoryQuery } from '../reports-api';

// Domyślny próg nieaktywności opaski w godzinach - musi być zgodny z User::$inactivityTimeoutHours w api2
export const DEFAULT_INACTIVITY_TIMEOUT_HOURS = 12;

const INACTIVITY_TIMEOUT_STORAGE_KEY = 'inactivityTimeoutHours';

/**
 * Filtry paginowanej listy opiekunów. `limit` 1–200; puste wartości wycina
 * `toQueryParams`, bo backend odpowiada 400 na nieznaną wartość filtra — a pusty
 * string jest właśnie taką wartością.
 */
export interface CaregiversQuery {
  /** Fraza: tokeny łączone przez AND, każdy trafia w imię ALBO nazwisko. */
  q?: string;
  /** Identyfikatory projektów po przecinku; opiekun przypisany do KTÓREGOKOLWIEK z nich. */
  project?: string;
  /** name | status | register_date */
  sort?: string;
  /** asc | desc */
  dir?: string;
  limit?: number;
  offset?: number;
}


/**
 * Ten sam projekt potrafi przyjść w wierszu kilka razy: `caregivers_projects` nie ma
 * unikalnego indeksu, a przypisanie hurtem wpisuje każdą parę dwa razy — lista rysuje
 * wtedy tyle plakietek, ile wierszy siedzi w bazie, i jeden projekt wygląda jak dwa
 * (SB-3131). Zwijamy po id tutaj, na wejściu z `/caregivers`, bo duplikat jest wadą
 * danych, a nie jednego ekranu — inaczej każdy kolejny widok plakietek musiałby o nim
 * pamiętać osobno.
 *
 * Wiersze zmieniamy w miejscu: odpowiedź to świeżo sparsowany JSON, którego poza tym
 * strumieniem nikt jeszcze nie widział.
 */
export function dedupeProjects(response: any): any {
  const rows = response && response.data ? response.data.items : null;

  if (!Array.isArray(rows)) {
    return response;
  }

  rows.forEach(row => {
    if (!row || !Array.isArray(row.projects)) {
      return;
    }

    const seen = new Set<any>();

    row.projects = row.projects.filter(project => {
      const id = project ? project.id : null;

      if (seen.has(id)) {
        return false;
      }

      seen.add(id);

      return true;
    });
  });

  return response;
}

@Injectable()
export class CaregiverService {

  private inactivityTimeoutHours$: Observable<number>;

  constructor(
    private apiService: ApiService
  ) { }

  getCaregivers(): Observable<any> {
    return this.apiService.get('/user/showCaregiver');
  }

  getCaregiver(id): Observable<any> {
    return this.apiService.get(`/user/showCaregiver/${id}`);
  }

  /**
   * Paginowana lista opiekunów managera — NOWA trasa obok `/user/showCaregiver`,
   * która zostaje nietknięta: to publiczny endpoint o konsumentach niewidocznych
   * z tego repo.
   *
   * Koperta odpowiedzi jest ta sama co w /alarms i /reports/*, więc czyta ją `readPage`.
   * Zakres wierszy, odsiew kont `type = 'Observer'` i kolejność (`status` malejąco,
   * potem nazwisko) należą do backendu — filtr działający na pobranej stronie
   * odpowiadałby na inne pytanie niż zadał użytkownik.
   */
  findCaregivers(query: CaregiversQuery = {}): Observable<any> {
    return this.apiService.get('/caregivers', toQueryParams(query))
      .pipe(map(dedupeProjects));
  }

  findAllCaregivers(): Observable<any[]> {
    const pageAt = (offset: number) => this.findCaregivers({ sort: 'name', limit: 200, offset: offset })
      .pipe(map(response => readPage(response)));

    return pageAt(0).pipe(
      expand(page => page.hasMore ? pageAt(page.offset + page.limit) : EMPTY),
      reduce((caregivers: any[], page) => caregivers.concat(page.items), [])
    );
  }

  addCaregiver(data): Observable<any> {
    return this.apiService.post('/user/createCaregiver', data);
  }

  editCaregiver(caregiver): Observable<any> {
    return this.apiService.put(`/user/updateCaregiver/${caregiver.id}`, caregiver);
  }

  deleteCaregiver(id): Observable<any> {
    return this.apiService.delete(`/manager/caregiver/${id}`);
  }

  setCaregiverAutoAssign(id: number, enabled: boolean): Observable<any> {
    return this.apiService.put(`/manager/caregivers/${id}/auto-assign`, { enabled: enabled });
  }

  workHours(data): Observable<any> {
    return this.apiService.post('/manager/caregiver/work-hours', data);
  }

  careHours(data): Observable<any> {
    return this.apiService.post('/manager/care-hours', data);
  }

  assignUser(data): Observable<any> {
    return this.apiService.post('/manager/assign-patient-to-caregiver', data);
  }

  assignAllUser(data): Observable<any> {
    return this.apiService.post('/manager/assign-all-patient-to-caregiver', data);
  }

  deleteUser(data): Observable<any> {
    return this.apiService.delete('/manager/remove-patient-from-caregiver', data);
  }

  tempFinishAlarm(data): Observable<any> {
    return this.apiService.post('/alarm/tempFinish', data);
  }

  tempEbsFinishAlarm(data): Observable<any> {
    return this.apiService.post('/alarm/tempEbsFinish', data);
  }

  finishAlarm(data): Observable<any> {
    return this.apiService.post('/alarm/finish', data);
  }

  finishAlarmEBS(data): Observable<any> {
    return this.apiService.post('/alarm/finishEBS', data);
  }

  /** PKOS: seniorzy czekający na połączenie aktywacyjne / kontrolne; z alarmId — stan seniora z alarmu i podpowiedź rodzaju. */
  getTestCalls(alarmId?: number): Observable<any> {
    return this.apiService.get('/caregiver/test-calls', alarmId ? { alarm: String(alarmId) } : {});
  }

  getCaregiverToAssignProject(id): Observable<any> {
    return this.apiService.get(`/manager/available-caregivers-to-assign-project/${id}`);
  }

  getCaregiverAssignedToProject(id): Observable<any> {
    return this.apiService.get(`/manager/caregivers-assigned-to-project/${id}`);
  }

  assignAllCaregiversToProject(data): Observable<any> {
    return this.apiService.post('/manager/assign-all-caregivers-to-project', data);
  }

  assignCaregiverToProject(data): Observable<any> {
    return this.apiService.post('/manager/assign-caregiver-to-project', data);
  }

  deleteCaregiverFromProject(data): Observable<any> {
    return this.apiService.post('/manager/delete-caregiver-from-project', data);
  }

  generateReport(type, form, lang, format: 'xlsx' | 'pdf'): Observable<any> {
    return this.apiService.post(`/manager/generateReport/${type}`, { form, lang, format });
  }

  getImeiChanges(): Observable<any> {
    return this.apiService.get(`/manager/getImeiChanges`);
  }

  getImeiChangesUser(imei): Observable<any> {
    return this.apiService.get(`/manager/getImeiChangesUser/${imei}`);
  }

  getHistoryOfBand(): Observable<any> {
    return this.apiService.get(`/manager/getHistoryOfBand`);
  }

  getOtherSoses(imei): Observable<any> {
    return this.apiService.get(`/getAllAlarmsNotHandled/${imei}`);
  }

  setPermissions(data): Observable<any> {
    return this.apiService.post('/manager/setPermissions', data);
  }

  getWorkHistory(managerId): Observable<any> {
    return this.apiService.get(`/manager/getWorkHistory/${managerId}`);
  }

  // --- Nowe API raportów: paginacja, wyszukiwanie i sortowanie po stronie serwera ----
  // Odpowiedź w tej samej kopercie co /alarms — { status, data: { items, pagination } } —
  // więc czyta ją `readPage` z alarms-api bez żadnego rozgałęziania. Zakres wierszy
  // wynika z tokenu, dlatego trasy nie biorą już id managera w ścieżce. Filtry idą
  // przez toQueryParams, bo backend odpowiada 400 na nieznaną wartość filtra —
  // a pusty string jest właśnie taką wartością.

  findWorkHistory(query: WorkHistoryQuery = {}): Observable<any> {
    return this.apiService.get('/reports/work-history', toQueryParams(query));
  }

  findImeiChanges(query: ImeiChangesQuery = {}): Observable<any> {
    return this.apiService.get('/reports/imei-changes', toQueryParams(query));
  }

  findBandHistory(query: BandHistoryQuery = {}): Observable<any> {
    return this.apiService.get('/reports/band-history', toQueryParams(query));
  }

  exportWorkHistory(managerId, lang): Observable<any> {
    return this.apiService.post(`/manager/exportWorkHistory/${lang}`, { managerId: managerId });
  }

  getCaregiverProjects(caregiverId): Observable<any> {
    return this.apiService.get(`/manager/getProjects/${caregiverId}`);
  }

  getProjectsDates(projectId): Observable<any> {
    return this.apiService.get(`/projects/getDates/${projectId}`);
  }

  getBandType(imei): Observable<any> {
    return this.apiService.get(`/manager/getBandType/${imei}`);
  }

  getManagerPrivilege(user_id): Observable<any> {
    return this.apiService.get(`/caregiver/getManagerPrivilege/${user_id}`);
  }

  getNotifsWithConnected(): Observable<any> {
    return this.apiService.get(`/manager/getNotifsWithConnected`);
  }

  // traitId = id encji TraitAlarm (backend czyta wyłącznie userId + traitId)
  markAsReadTrait(userId, traitId): Observable<any> {
    return this.apiService.post(`/manager/markAsReadTrait`, { userId: userId, traitId: traitId });
  }

  getDataOfManager(): Observable<any> {
    return this.apiService.get(`/manager/getDataOfManager`);
  }

  getActivityMonthly(): Observable<any> {
    return this.apiService.get(`/manager/activity-monthly`);
  }

  getActivityMonthlyCaregiver(): Observable<any> {
    return this.apiService.get(`/manager/activity-monthly-caregiver`);
  }

  refreshActivitySnapshot(): Observable<any> {
    return this.apiService.post(`/manager/refreshActivitySnapshot`, {});
  }

  getDataOfCaregiver(): Observable<any> {
    return this.apiService.get(`/manager/getDataOfCaregiver`);
  }

  getLastNotifsNormal(): Observable<any> {
    return this.apiService.get(`/manager/getLastNotifsNormal`);
  }

  getLastNotifsTraits(all: boolean = false): Observable<any> {
    return this.apiService.get(`/manager/getLastNotifsTraits?all=${all}`);
  }

  getLastNotifsNormalCaregiver(): Observable<any> {
    return this.apiService.get(`/caregiver/getLastNotifsNormalCaregiver`);
  }

  getLastNotifsTraitsCaregiver(all: boolean = false): Observable<any> {
    return this.apiService.get(`/caregiver/getLastNotifsTraitsCaregiver?all=${all}`);
  }

  getUsersOfMapLocal(): Observable<any> {
    return this.apiService.get(`/manager/getUsersOfMapLocal`);
  }

  getUserOfMapLocal(imei): Observable<any> {
    return this.apiService.get(`/manager/getUserOfMapLocal/${imei}`);
  }

  getUsersOfMapLocalCaregiver(): Observable<any> {
    return this.apiService.get(`/caregiver/getUsersOfMapLocal`);
  }

  getProjectList(): Observable<any> {
    return this.apiService.get('/manager/getProjectsList');
  }

  getInactiveBandsInProject(project, interval): Observable<any> {
    return this.apiService.get(`/manager/getInactiveBandsInProject/${project}/${interval}`);
  }

  setInactivityTimeout(hours: number): Observable<any> {
    return this.apiService.post('/caregiver/setInactivityTimeout', { hours: hours });
  }

  getInactivityTimeout(): Observable<any> {
    return this.apiService.get('/caregiver/getInactivityTimeout');
  }

  /**
   * Próg nieaktywności opaski (w godzinach) ustawiony w Ustawienia -> Zarządzanie.
   * Cache'owane, bo pyta o to każdy wiersz listy użytkowników z osobna.
   */
  getInactivityTimeoutHours(): Observable<number> {
    if (!this.inactivityTimeoutHours$) {
      this.inactivityTimeoutHours$ = this.getInactivityTimeout().pipe(
        map(response => {
          const hours = response && response.data ? Number(response.data.hours) : NaN;
          return hours > 0 ? hours : DEFAULT_INACTIVITY_TIMEOUT_HOURS;
        }),
        tap(hours => localStorage.setItem(INACTIVITY_TIMEOUT_STORAGE_KEY, String(hours))),
        catchError(() => of(DEFAULT_INACTIVITY_TIMEOUT_HOURS)),
        shareReplay(1)
      );
    }

    return this.inactivityTimeoutHours$;
  }

  /**
   * Ostatnio znany próg, czytany synchronicznie. Bez tego pierwsza klatka listy
   * renderuje się domyślnymi 12 h i status przeskakuje, gdy dojedzie odpowiedź z API.
   * null = nie znamy jeszcze progu na tej przeglądarce.
   */
  getCachedInactivityTimeoutHours(): number {
    const stored = Number(localStorage.getItem(INACTIVITY_TIMEOUT_STORAGE_KEY));

    return stored > 0 ? stored : null;
  }

  /** Po zapisie suwaka - nowa wartość obowiązuje od razu, bez ponownego requestu. */
  primeInactivityTimeoutHours(hours: number): void {
    localStorage.setItem(INACTIVITY_TIMEOUT_STORAGE_KEY, String(hours));
    this.inactivityTimeoutHours$ = of(hours);
  }

  /** Wywoływane przy wylogowaniu - kolejny użytkownik nie może dziedziczyć cudzego progu. */
  clearInactivityTimeoutCache(): void {
    localStorage.removeItem(INACTIVITY_TIMEOUT_STORAGE_KEY);
    this.inactivityTimeoutHours$ = null;
  }

}
